// Coverage Map — Phase 2.3 of Federal Template Parity sprint.
//
// Read-only join across the 4 federal-template tools by Task Area:
//   1. Extracted Requirements  (with self-score distribution)
//   2. Capability Matrix LCATs (with status distribution)
//   3. Gap Tracker rows        (currently bucket to Unassigned — see notice)
//   4. Compliance Matrix rows  (currently bucket to Unassigned — see notice)
//
// v3.12.3 — interactive drill-down: clickable cells open a side panel
// listing the underlying rows with one-click jump-to-source.
//
// Powered by GET /api/pursuits/{id}/coverage-map (matrix) and
//             GET /api/pursuits/{id}/coverage-map/cell (drill-down).

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../../components/ui/sheet';
import { Map, ArrowLeft, AlertCircle, FileText, Users, Layers, Shield, ExternalLink, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SEG_COLOR = {
  red: 'bg-red-500',
  yellow: 'bg-amber-400',
  green: 'bg-emerald-500',
  blue: 'bg-blue-500',
  unscored: 'bg-slate-200',
};

function StackedBar({ distribution, total }) {
  if (!total) return <span className="text-[10px] text-slate-300 italic">—</span>;
  const segs = ['red', 'yellow', 'green', 'blue', 'unscored'];
  return (
    <TooltipProvider delayDuration={200}>
      <div className="flex h-1.5 rounded overflow-hidden ring-1 ring-slate-200 w-full">
        {segs.map(key => {
          const count = distribution[key] || 0;
          if (count <= 0) return null;
          const pct = (count / total) * 100;
          return (
            <Tooltip key={key}>
              <TooltipTrigger asChild>
                <div className={cn(SEG_COLOR[key], 'transition-opacity hover:opacity-80')} style={{ width: `${pct}%` }} />
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">
                {key}: {count} ({Math.round(pct)}%)
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}

// Per-key color tone. Keys that signal problems get red/amber, healthy
// keys get emerald, neutral keys get slate. Mapped uniformly for both
// gaps and compliance pills so the visual signal is consistent.
const PILL_TONE = {
  // problematic
  gap: 'bg-red-50 text-red-700 border-red-200',
  open: 'bg-red-50 text-red-700 border-red-200',
  non_compliant: 'bg-red-50 text-red-700 border-red-200',
  // partials / cautions
  partial: 'bg-amber-50 text-amber-700 border-amber-200',
  sow_restate: 'bg-purple-50 text-purple-700 border-purple-200',
  // healthy
  met: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  compliant: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

function StatusPills({ counts, labels, onPillClick, problematicKeys }) {
  // counts: { open: 5, partial: 2, met: 1 }, labels: { open: 'Gap', partial: 'Partial', met: 'Met' }
  // v3.12.3 — when onPillClick is provided AND the key is "problematic"
  // (gap/open/non_compliant/sow_restate), the pill becomes a clickable
  // button that drills into the underlying rows.
  const entries = Object.entries(counts).filter(([k]) => labels[k]);
  if (entries.every(([, v]) => v === 0)) return <span className="text-[10px] text-slate-300 italic">—</span>;
  const isProblematic = (k) => problematicKeys && problematicKeys.includes(k);
  return (
    <div className="flex flex-wrap gap-1">
      {entries.map(([k, v]) => v > 0 ? (
        <button
          type="button"
          key={k}
          onClick={onPillClick && isProblematic(k) ? (e) => { e.stopPropagation(); onPillClick(k); } : undefined}
          disabled={!(onPillClick && isProblematic(k))}
          data-testid={onPillClick && isProblematic(k) ? `cov-pill-${k}` : undefined}
          className={cn(
            'text-[10px] px-1.5 py-0.5 rounded border font-medium transition-colors',
            PILL_TONE[k] || 'bg-slate-50 text-slate-700 border-slate-200',
            onPillClick && isProblematic(k) && 'hover:opacity-80 cursor-pointer ring-1 ring-transparent hover:ring-current/30',
          )}
        >
          {v} {labels[k]}
        </button>
      ) : null)}
    </div>
  );
}

export default function CoverageMapLive() {
  const { workspaceId, pursuitId } = useParams();
  const navigate = useNavigate();
  const { authToken } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // v3.12.3 — drill-down side panel state
  const [drill, setDrill] = useState({
    open: false,
    taskArea: '',          // raw bucket key (may be __unassigned__)
    taskAreaLabel: '',     // user-facing label
    tool: '',              // requirements | lcats | gaps | compliance
    status: null,          // sub-filter (e.g. NON_COMPLIANT)
    title: '',             // panel header text
    rows: [],
    jumpRoute: '',
    loading: false,
  });

  const openDrill = async (taskArea, taskAreaLabel, tool, status, title) => {
    const bucketKey = taskAreaLabel === 'Unassigned' || !taskAreaLabel
      ? '__unassigned__'
      : taskArea;
    setDrill({
      open: true,
      taskArea: bucketKey,
      taskAreaLabel,
      tool,
      status,
      title,
      rows: [],
      jumpRoute: '',
      loading: true,
    });
    try {
      const url = new URL(
        `${API_URL}/api/pursuits/${pursuitId}/coverage-map/cell`,
      );
      url.searchParams.set('task_area', bucketKey);
      url.searchParams.set('tool', tool);
      if (status) url.searchParams.set('status', status);
      const r = await fetch(url.toString(), {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (r.ok) {
        const json = await r.json();
        setDrill(prev => ({
          ...prev,
          rows: json.rows || [],
          jumpRoute: json.jump_route,
          loading: false,
        }));
      } else {
        setDrill(prev => ({ ...prev, rows: [], loading: false }));
      }
    } catch (_) {
      setDrill(prev => ({ ...prev, rows: [], loading: false }));
    }
  };

  // Map a (jumpRoute, rowId) to the in-app URL with ?focus={id} so the
  // target page can scroll to + highlight the row. Note: in this app
  // requirements live inside the Opportunity Intake page and the
  // capability-matrix lives inside the Gap Analysis page (they aren't
  // separately routed yet), so we deep-link into those parent pages.
  const jumpToSource = (route, rowId) => {
    const map = {
      requirements: `/${workspaceId}/pursuit/${pursuitId}/intake?focus=${rowId}`,
      'capability-matrix': `/${workspaceId}/pursuit/${pursuitId}/gaps?tab=capability-matrix&focus=${rowId}`,
      'gap-analysis': `/${workspaceId}/pursuit/${pursuitId}/gaps?focus=${rowId}`,
      compliance: `/${workspaceId}/pursuit/${pursuitId}/compliance?focus=${rowId}`,
    };
    const url = map[route];
    if (url) navigate(url);
  };

  useEffect(() => {
    if (!pursuitId || !authToken) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API_URL}/api/pursuits/${pursuitId}/coverage-map`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (r.ok && !cancelled) setData(await r.json());
      } catch (_) {}
      finally { if (!cancelled) setLoading(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [pursuitId, authToken]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="coverage-map-page">
      {/* Header — v3.18.24 canonical: px-8 py-4 + max-w-7xl mx-auto */}
      <div className="bg-white border-b border-slate-200 px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/${workspaceId}/pursuit/${pursuitId}/gonogo`)}
              className="mb-2 -ml-2 h-7 px-2"
              data-testid="coverage-map-back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to Go/No-Go
            </Button>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-100 rounded-lg flex-shrink-0">
                <Map className="w-5 h-5 text-cyan-700" />
              </div>
              <div>
                <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Cross-Tool Coverage Map</h1>
                <p className="text-sm text-slate-500">
                  One view across Requirements · LCATs · Gaps · Compliance, joined by federal Task Area.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-8">
        <div className="max-w-7xl mx-auto space-y-6">

      {loading && <p className="text-sm text-slate-500">Loading…</p>}

      {!loading && data && data.totals && (
        <>
          {/* Totals strip */}
          <div className="grid grid-cols-4 gap-3">
            <Card><CardContent className="p-3 flex items-center gap-2">
              <FileText className="w-4 h-4 text-slate-500" />
              <div><p className="text-[10px] uppercase tracking-wider text-slate-500">Requirements</p>
              <p className="text-xl font-semibold text-slate-800">{data.totals.requirements}</p></div>
            </CardContent></Card>
            <Card><CardContent className="p-3 flex items-center gap-2">
              <Users className="w-4 h-4 text-slate-500" />
              <div><p className="text-[10px] uppercase tracking-wider text-slate-500">LCATs</p>
              <p className="text-xl font-semibold text-slate-800">{data.totals.lcats}</p></div>
            </CardContent></Card>
            <Card><CardContent className="p-3 flex items-center gap-2">
              <Layers className="w-4 h-4 text-slate-500" />
              <div><p className="text-[10px] uppercase tracking-wider text-slate-500">Gaps</p>
              <p className="text-xl font-semibold text-slate-800">{data.totals.gaps}</p></div>
            </CardContent></Card>
            <Card><CardContent className="p-3 flex items-center gap-2">
              <Shield className="w-4 h-4 text-slate-500" />
              <div><p className="text-[10px] uppercase tracking-wider text-slate-500">Compliance</p>
              <p className="text-xl font-semibold text-slate-800">{data.totals.compliance_entries}</p></div>
            </CardContent></Card>
          </div>

          {/* Notice about gap_analysis / rtm_matrix not yet carrying task_area */}
          {(data.notes && (!data.notes.gaps_carry_task_area || !data.notes.compliance_carries_task_area)) && (
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4 text-xs text-amber-800">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <p>
                Gap Tracker rows and Compliance Matrix entries don't carry a <code className="font-mono">task_area</code> field yet — they bucket into <strong>Unassigned</strong> for now.
                Phase 1.2 already added <code className="font-mono">task_area</code> to LCATs; extending the same to Gaps & Compliance is on the backlog.
              </p>
            </div>
          )}

          {/* The matrix */}
          {data.task_areas.length === 0 ? (
            <Card><CardContent className="p-12 text-center text-slate-400">
              No data yet. Run the AI Shredder on this pursuit's RFP to populate Task Areas.
            </CardContent></Card>
          ) : (
            <Card>
              <CardHeader className="py-3 border-b border-slate-200">
                <CardTitle className="text-sm">Task Area Roll-up</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <table className="w-full text-sm" data-testid="coverage-map-table">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50">
                      <th className="text-left p-3 w-[20%]">Task Area</th>
                      <th className="text-left p-3">Requirements (Self-Score)</th>
                      <th className="text-left p-3">LCATs (Status)</th>
                      <th className="text-left p-3">Gaps</th>
                      <th className="text-left p-3">Compliance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.task_areas.map(ta => (
                      <tr
                        key={ta.name}
                        data-testid={`coverage-row-${ta.name.replace(/\s+/g, '-')}`}
                        className={cn(
                          'border-t border-slate-100',
                          ta.is_unassigned && 'bg-slate-50/40'
                        )}
                      >
                        <td className="p-3 align-top">
                          <p className={cn(
                            'font-medium text-sm',
                            ta.is_unassigned ? 'text-slate-500 italic' : 'text-slate-800'
                          )}>
                            {ta.name}
                          </p>
                        </td>
                        <td
                          className="p-3 align-top cursor-pointer hover:bg-slate-50 transition-colors"
                          onClick={() => openDrill(ta.name, ta.is_unassigned ? 'Unassigned' : ta.name, 'requirements', null, `Requirements · ${ta.is_unassigned ? 'Unassigned' : ta.name}`)}
                          data-testid={`cov-cell-requirements-${ta.name.replace(/\s+/g, '-')}`}
                          title="Click to drill into rows"
                        >
                          <div className="space-y-1">
                            <div className="flex items-center justify-between gap-3">
                              <StackedBar distribution={ta.requirements.distribution} total={ta.requirements.count} />
                              <span className="text-[10px] text-slate-500 shrink-0 w-[80px] text-right">
                                {ta.requirements.scored}/{ta.requirements.count} scored
                              </span>
                            </div>
                            <p className="text-[10px] text-slate-400">
                              <span className="text-emerald-600 font-medium">{ta.requirements.strong}</span> strong (≥2)
                              {(ta.requirements.distribution.red > 0) && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openDrill(ta.name, ta.is_unassigned ? 'Unassigned' : ta.name, 'requirements', 'red', `Red-scored requirements · ${ta.is_unassigned ? 'Unassigned' : ta.name}`);
                                  }}
                                  className="ml-2 text-red-600 hover:underline font-medium"
                                  data-testid={`cov-pill-requirements-red-${ta.name.replace(/\s+/g, '-')}`}
                                >
                                  · {ta.requirements.distribution.red} red
                                </button>
                              )}
                            </p>
                          </div>
                        </td>
                        <td
                          className="p-3 align-top cursor-pointer hover:bg-slate-50 transition-colors"
                          onClick={() => openDrill(ta.name, ta.is_unassigned ? 'Unassigned' : ta.name, 'lcats', null, `LCATs · ${ta.is_unassigned ? 'Unassigned' : ta.name}`)}
                          data-testid={`cov-cell-lcats-${ta.name.replace(/\s+/g, '-')}`}
                          title="Click to drill into rows"
                        >
                          {ta.lcats.count === 0 ? (
                            <span className="text-[10px] text-slate-300 italic">—</span>
                          ) : (
                            <>
                              <StatusPills
                                counts={{ met: ta.lcats.met, partial: ta.lcats.partial, gap: ta.lcats.gap }}
                                labels={{ met: 'Met', partial: 'Partial', gap: 'Gap' }}
                                onPillClick={(k) => openDrill(ta.name, ta.is_unassigned ? 'Unassigned' : ta.name, 'lcats', k.toUpperCase(), `LCATs ${k.toUpperCase()} · ${ta.is_unassigned ? 'Unassigned' : ta.name}`)}
                                problematicKeys={['gap']}
                              />
                              {ta.lcats.high_severity > 0 && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openDrill(ta.name, ta.is_unassigned ? 'Unassigned' : ta.name, 'lcats', 'HIGH', `HIGH-severity LCATs · ${ta.is_unassigned ? 'Unassigned' : ta.name}`);
                                  }}
                                  className="mt-1 inline-flex"
                                  data-testid={`cov-pill-lcats-high-${ta.name.replace(/\s+/g, '-')}`}
                                >
                                  <Badge className="text-[10px] bg-red-50 text-red-700 border-red-200 hover:bg-red-100 cursor-pointer">
                                    {ta.lcats.high_severity} HIGH
                                  </Badge>
                                </button>
                              )}
                            </>
                          )}
                        </td>
                        <td
                          className="p-3 align-top cursor-pointer hover:bg-slate-50 transition-colors"
                          onClick={() => openDrill(ta.name, ta.is_unassigned ? 'Unassigned' : ta.name, 'gaps', null, `Gaps · ${ta.is_unassigned ? 'Unassigned' : ta.name}`)}
                          data-testid={`cov-cell-gaps-${ta.name.replace(/\s+/g, '-')}`}
                          title="Click to drill into rows"
                        >
                          <StatusPills
                            counts={{ open: ta.gaps.open, partial: ta.gaps.partial, met: ta.gaps.met }}
                            labels={{ open: 'Open', partial: 'Partial', met: 'Met' }}
                            onPillClick={(k) => openDrill(ta.name, ta.is_unassigned ? 'Unassigned' : ta.name, 'gaps', k.toUpperCase(), `Gaps ${k.toUpperCase()} · ${ta.is_unassigned ? 'Unassigned' : ta.name}`)}
                            problematicKeys={['open']}
                          />
                        </td>
                        <td
                          className="p-3 align-top cursor-pointer hover:bg-slate-50 transition-colors"
                          onClick={() => openDrill(ta.name, ta.is_unassigned ? 'Unassigned' : ta.name, 'compliance', null, `Compliance · ${ta.is_unassigned ? 'Unassigned' : ta.name}`)}
                          data-testid={`cov-cell-compliance-${ta.name.replace(/\s+/g, '-')}`}
                          title="Click to drill into rows"
                        >
                          <StatusPills
                            counts={{
                              compliant: ta.compliance.compliant,
                              partial: ta.compliance.partial,
                              non_compliant: ta.compliance.non_compliant,
                              sow_restate: ta.compliance.sow_restate,
                            }}
                            labels={{
                              compliant: 'Compliant',
                              partial: 'Partial',
                              non_compliant: 'Non-Compliant',
                              sow_restate: 'SOW Restate',
                            }}
                            onPillClick={(k) => openDrill(ta.name, ta.is_unassigned ? 'Unassigned' : ta.name, 'compliance', k.toUpperCase(), `Compliance ${k.toUpperCase()} · ${ta.is_unassigned ? 'Unassigned' : ta.name}`)}
                            problematicKeys={['non_compliant', 'sow_restate']}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* v3.12.3 — Drill-down side panel */}
      <Sheet
        open={drill.open}
        onOpenChange={(o) => setDrill((p) => ({ ...p, open: o }))}
      >
        <SheetContent
          side="right"
          className="w-full sm:max-w-lg overflow-y-auto"
          data-testid="cov-drill-panel"
        >
          <SheetHeader>
            <SheetTitle data-testid="cov-drill-title">{drill.title}</SheetTitle>
            <SheetDescription>
              {drill.loading ? 'Loading rows…' : `${drill.rows.length} row${drill.rows.length === 1 ? '' : 's'}`}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-4 space-y-2">
            {drill.loading ? (
              <div className="flex items-center justify-center py-12 text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Fetching…
              </div>
            ) : drill.rows.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-12">
                No rows match this filter.
              </p>
            ) : (
              drill.rows.map((row) => (
                <div
                  key={row.id}
                  className="border border-slate-200 rounded-md p-3 hover:border-slate-300 transition-colors"
                  data-testid={`cov-drill-row-${row.id}`}
                >
                  <div className="flex items-start gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900">
                        {row.title}
                      </p>
                      {row.subtitle && (
                        <p className="text-xs text-slate-500 mt-0.5">{row.subtitle}</p>
                      )}
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        <Badge variant="outline" className="text-[10px]">
                          {row.status_label}
                        </Badge>
                        {row.severity && (
                          <Badge
                            variant="outline"
                            className={cn(
                              'text-[10px]',
                              row.severity === 'HIGH' && 'bg-red-50 text-red-700 border-red-200',
                              row.severity === 'MEDIUM' && 'bg-amber-50 text-amber-700 border-amber-200',
                              row.severity === 'LOW' && 'bg-slate-50 text-slate-700 border-slate-200',
                            )}
                          >
                            {row.severity}
                          </Badge>
                        )}
                        {row.gap_severity === 'HIGH' && (
                          <Badge className="text-[10px] bg-red-50 text-red-700 border-red-200">
                            HIGH severity
                          </Badge>
                        )}
                        {row.sow_restate_flag && (
                          <Badge className="text-[10px] bg-purple-50 text-purple-700 border-purple-200">
                            SOW restate
                          </Badge>
                        )}
                        {row.required_qty != null && (
                          <Badge variant="outline" className="text-[10px]">
                            {row.available_qty || 0}/{row.required_qty} avail
                          </Badge>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => jumpToSource(drill.jumpRoute, row.id)}
                      className="flex-shrink-0"
                      data-testid={`cov-drill-jump-${row.id}`}
                      title="Open in source tool"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </SheetContent>
      </Sheet>
        </div>
      </div>
    </div>
  );
}
